// SmsConfigViewPage.js
import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    CircularProgress,
    Alert,
    Box,
    InputAdornment,
    IconButton,
    Card,
    CardContent,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Divider,
    Tooltip,
    Snackbar,
    Fab,
    Tabs,
    Tab,
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Clear as ClearIcon,
    Sms as SmsIcon,
    Person as PersonIcon,
    Lock as LockIcon,
    Send as SendIcon,
    Close as CloseIcon,
    Save as SaveIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import AuthService from '../Auth/AuthService';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
    borderRadius: theme.spacing(2),
    boxShadow: '0 3px 10px rgba(0,0,0,0.08)',
}));

const SearchPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    backgroundColor: '#f8fafc',
    borderRadius: theme.spacing(2),
}));

const ActionFab = styled(Fab)(({ theme }) => ({
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
}));

const SmsConfigViewPage = () => {
    // State
    const [smsConfigs, setSmsConfigs] = useState([]);
    const [filteredConfigs, setFilteredConfigs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [tabValue, setTabValue] = useState(0);
    const [showPassword, setShowPassword] = useState(false);

    // Dialog states
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState('add');
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [configToDelete, setConfigToDelete] = useState(null);
    const [testDialogOpen, setTestDialogOpen] = useState(false);
    const [testMobile, setTestMobile] = useState('');
    const [testMessage, setTestMessage] = useState('');
    const [sendingTest, setSendingTest] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        senderid: '',
        channel: '',
        dcs: '0',
        flashsms: '0',
        route: '1',
        tempid: '',
        sendurl: '',
        toMobile: '',
        message: '',
        temptype: '',
    });

    const [formErrors, setFormErrors] = useState({});

    // Template options
    const templateOptions = [
        { value: 'OTP', label: 'OTP Verification' },
        { value: 'PROMOTIONAL', label: 'Promotional' },
        { value: 'TRANSACTIONAL', label: 'Transactional' },
        { value: 'ALERT', label: 'Alert' },
        { value: 'REMINDER', label: 'Reminder' },
    ];

    // Channel options
    const channelOptions = [
        { value: 'SMS', label: 'SMS' },
        { value: 'WHATSAPP', label: 'WhatsApp' },
        { value: 'EMAIL', label: 'Email' },
    ];

    // Route options
    const routeOptions = [
        { value: '1', label: 'Route 1 - Promotional' },
        { value: '2', label: 'Route 2 - Transactional' },
        { value: '3', label: 'Route 3 - OTP' },
    ];

    // Fetch all SMS configs
    const fetchSmsConfigs = async () => {
        setLoading(true);
        setError('');
        try {
            const rest = AuthService.pathUrl("GET", "/sms-config");
            const response = await AuthService.getAuth(rest);

            if (response) {
                setSmsConfigs(response);
                setFilteredConfigs(response);
                setTotalRecords(response.length);
            }
        } catch (err) {
            setError('Failed to fetch SMS configurations. Please try again.');
            console.error('Error fetching SMS configs:', err);
        } finally {
            setLoading(false);
        }
    };

    // Search SMS configs
    const handleSearch = () => {
        if (!searchTerm.trim()) {
            setFilteredConfigs(smsConfigs);
            setTotalRecords(smsConfigs.length);
            return;
        }

        const filtered = smsConfigs.filter(config =>
            config.username && config.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
            config.senderid && config.senderid.toLowerCase().includes(searchTerm.toLowerCase()) ||
            config.channel && config.channel.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredConfigs(filtered);
        setTotalRecords(filtered.length);
        setPage(0);
    };

    // Handle search on Enter key
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // Clear search
    const handleClearSearch = () => {
        setSearchTerm('');
        setFilteredConfigs(smsConfigs);
        setTotalRecords(smsConfigs.length);
        setPage(0);
    };

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        if (newValue === 0) {
            setFilteredConfigs(smsConfigs);
        } else if (newValue === 1) {
            // Show only OTP templates
            const filtered = smsConfigs.filter(config => config.temptype === 'OTP');
            setFilteredConfigs(filtered);
        } else if (newValue === 2) {
            // Show only promotional
            const filtered = smsConfigs.filter(config => config.temptype === 'PROMOTIONAL');
            setFilteredConfigs(filtered);
        }
        setTotalRecords(filteredConfigs.length);
        setPage(0);
    };

    // Open add dialog
    const handleAddClick = () => {
        setDialogMode('add');
        setFormData({
            username: '',
            password: '',
            senderid: '',
            channel: 'SMS',
            dcs: '0',
            flashsms: '0',
            route: '1',
            tempid: '',
            sendurl: '',
            toMobile: '',
            message: '',
            temptype: 'OTP',
        });
        setFormErrors({});
        setOpenDialog(true);
    };

    // Open edit dialog
    const handleEditClick = (config) => {
        setDialogMode('edit');
        setSelectedConfig(config);
        setFormData({
            username: config.username || '',
            password: config.password || '',
            senderid: config.senderid || '',
            channel: config.channel || 'SMS',
            dcs: config.dcs || '0',
            flashsms: config.flashsms || '0',
            route: config.route || '1',
            tempid: config.tempid || '',
            sendurl: config.sendurl || '',
            toMobile: config.toMobile || '',
            message: config.message || '',
            temptype: config.temptype || 'OTP',
        });
        setFormErrors({});
        setOpenDialog(true);
    };

    // Handle delete
    const handleDeleteClick = (config) => {
        setConfigToDelete(config);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!configToDelete) return;

        setLoading(true);
        try {
            const rest = AuthService.pathUrl("DELETE", `/sms-config/${configToDelete.id}`);
            await AuthService.deleteAuth(rest);

            setSuccess('SMS configuration deleted successfully');
            fetchSmsConfigs();
            setDeleteDialogOpen(false);
            setConfigToDelete(null);
        } catch (err) {
            setError('Failed to delete SMS configuration. Please try again.');
            console.error('Error deleting SMS config:', err);
        } finally {
            setLoading(false);
        }
    };

    // Handle test SMS
    const handleTestClick = (config) => {
        setSelectedConfig(config);
        setTestMobile('');
        setTestMessage(config.message || 'Test message from school management system');
        setTestDialogOpen(true);
    };

    const sendTestSms = async () => {
        if (!testMobile.trim()) {
            setError('Please enter a mobile number');
            return;
        }

        setSendingTest(true);
        try {
            const payload = {
                configId: selectedConfig.id,
                toMobile: testMobile,
                message: testMessage,
            };

            const rest = AuthService.pathUrl("POST", "/sms-config");
            const response = await AuthService.postAuth(rest, payload);

            if (response) {
                setSuccess('Test SMS sent successfully!');
                setTestDialogOpen(false);
                setTestMobile('');
            }
        } catch (err) {
            setError('Failed to send test SMS. Please try again.');
            console.error('Error sending test SMS:', err);
        } finally {
            setSendingTest(false);
        }
    };

    // Handle form input changes
    const handleFormChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
        if (formErrors[field]) {
            setFormErrors({ ...formErrors, [field]: '' });
        }
    };

    // Validate form
    const validateForm = () => {
        const errors = {};

        if (!formData.username || !formData.username.trim()) {
            errors.username = 'Username is required';
        }
        if (!formData.password || !formData.password.trim()) {
            errors.password = 'Password is required';
        }
        if (!formData.senderid || !formData.senderid.trim()) {
            errors.senderid = 'Sender ID is required';
        }
        if (!formData.channel) {
            errors.channel = 'Channel is required';
        }
        if (!formData.route) {
            errors.route = 'Route is required';
        }
        if (!formData.temptype) {
            errors.temptype = 'Template type is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Save SMS config
    const handleSave = async () => {
        if (!validateForm()) return;

        setLoading(true);
        try {
            const data = { ...formData };

            let response;
            if (dialogMode === 'add') {
                const rest = AuthService.pathUrl("POST", "/sms-config");
                response = await AuthService.postAuth(rest, data);
            } else {
                const rest = AuthService.pathUrl("PUT", `/sms-config/${selectedConfig.id}`);
                response = await AuthService.putAuth(rest, data);
            }

            if (response) {
                setSuccess(dialogMode === 'add' ? 'SMS configuration created successfully' : 'SMS configuration updated successfully');
                fetchSmsConfigs();
                setOpenDialog(false);
                setSelectedConfig(null);
                setFormData({
                    username: '',
                    password: '',
                    senderid: '',
                    channel: '',
                    dcs: '0',
                    flashsms: '0',
                    route: '1',
                    tempid: '',
                    sendurl: '',
                    toMobile: '',
                    message: '',
                    temptype: '',
                });
            }
        } catch (err) {
            setError(`Failed to ${dialogMode === 'add' ? 'create' : 'update'} SMS configuration. Please try again.`);
            console.error('Error saving SMS config:', err);
        } finally {
            setLoading(false);
        }
    };

    // Load data on component mount
    useEffect(() => {
        fetchSmsConfigs();
    }, []);

    // Auto-search when search term changes
    useEffect(() => {
        if (searchTerm === '') {
            setFilteredConfigs(smsConfigs);
            setTotalRecords(smsConfigs.length);
        }
    }, [searchTerm, smsConfigs]);

    // Handle page change
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Copy to clipboard
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setSuccess('Copied to clipboard!');
        setTimeout(() => setSuccess(''), 3000);
    };

    // Get template type color
    const getTemplateColor = (type) => {
        const colors = {
            'OTP': 'primary',
            'PROMOTIONAL': 'warning',
            'TRANSACTIONAL': 'success',
            'ALERT': 'error',
            'REMINDER': 'info',
        };
        return colors[type] || 'default';
    };

    return (
        <Container maxWidth="xl">
            <StyledPaper>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box>
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#1a237e' }}>
                            <SmsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            SMS Configuration
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Manage SMS gateway configurations and templates
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddClick}
                        sx={{ height: 'fit-content' }}
                    >
                        Add Configuration
                    </Button>
                </Box>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab label="All Configurations" />
                        <Tab label="OTP Templates" />
                        <Tab label="Promotional" />
                    </Tabs>
                </Box>

                {/* Search Section */}
                <SearchPaper elevation={0}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                label="Search by Username, Sender ID or Channel"
                                variant="outlined"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Enter search term..."
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="action" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchTerm && (
                                            <InputAdornment position="end">
                                                <IconButton onClick={handleClearSearch} size="small">
                                                    <ClearIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Stack direction="row" spacing={4}>
                                <Button
                                    variant="contained"
                                    onClick={handleSearch}
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                                    fullWidth
                                >
                                    Search
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={fetchSmsConfigs}
                                    disabled={loading}
                                    startIcon={<RefreshIcon />}
                                >

                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </SearchPaper>

                {/* Alerts */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}

                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={3}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Total Configurations
                                </Typography>
                                <Typography variant="h4">
                                    {smsConfigs.length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Card variant="outlined" sx={{ borderColor: 'primary.main' }}>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    OTP Templates
                                </Typography>
                                <Typography variant="h4" color="primary.main">
                                    {smsConfigs.filter(c => c.temptype === 'OTP').length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Card variant="outlined" sx={{ borderColor: 'warning.main' }}>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Promotional
                                </Typography>
                                <Typography variant="h4" color="warning.main">
                                    {smsConfigs.filter(c => c.temptype === 'PROMOTIONAL').length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Card variant="outlined" sx={{ borderColor: 'success.main' }}>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Active Configs
                                </Typography>
                                <Typography variant="h4" color="success.main">
                                    {smsConfigs.filter(c => c.isActive !== false).length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Table Section */}
                <TableContainer component={Paper} variant="outlined">
                    <Table>
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell><strong>ID</strong></TableCell>
                                <TableCell><strong>Username</strong></TableCell>
                                <TableCell><strong>Sender ID</strong></TableCell>
                                <TableCell><strong>Channel</strong></TableCell>
                                <TableCell><strong>Route</strong></TableCell>
                                <TableCell><strong>Template Type</strong></TableCell>
                                <TableCell align="center"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <CircularProgress />
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            Loading SMS configurations...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : filteredConfigs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body1" color="textSecondary">
                                            No SMS configurations found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredConfigs
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((config) => (
                                        <TableRow key={config.id} hover>
                                            <TableCell>
                                                <Chip
                                                    label={`#${config.id}`}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <PersonIcon fontSize="small" color="action" />
                                                    {config.username}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={config.senderid}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={config.channel}
                                                    size="small"
                                                    color="info"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                Route {config.route}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={config.temptype}
                                                    size="small"
                                                    color={getTemplateColor(config.temptype)}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={1} justifyContent="center">
                                                    <Tooltip title="Test SMS">
                                                        <IconButton
                                                            color="success"
                                                            onClick={() => handleTestClick(config)}
                                                            size="small"
                                                        >
                                                            <SendIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            color="primary"
                                                            onClick={() => handleEditClick(config)}
                                                            size="small"
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            color="error"
                                                            onClick={() => handleDeleteClick(config)}
                                                            size="small"
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={totalRecords}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>
            </StyledPaper>

            {/* Add/Edit Dialog */}
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            {dialogMode === 'add' ? 'Add SMS Configuration' : 'Edit SMS Configuration'}
                        </Typography>
                        <IconButton onClick={() => setOpenDialog(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Username"
                                value={formData.username}
                                onChange={(e) => handleFormChange('username', e.target.value)}
                                error={!!formErrors.username}
                                helperText={formErrors.username}
                                required
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PersonIcon />
                                            </InputAdornment>
                                        ),
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                value={formData.password}
                                onChange={(e) => handleFormChange('password', e.target.value)}
                                error={!!formErrors.password}
                                helperText={formErrors.password}
                                required
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LockIcon />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowPassword(!showPassword)}>
                                                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Sender ID"
                                value={formData.senderid}
                                onChange={(e) => handleFormChange('senderid', e.target.value)}
                                error={!!formErrors.senderid}
                                helperText={formErrors.senderid}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth error={!!formErrors.channel} required>
                                <InputLabel>Channel</InputLabel>
                                <Select
                                    value={formData.channel}
                                    onChange={(e) => handleFormChange('channel', e.target.value)}
                                    label="Channel"
                                >
                                    {channelOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth error={!!formErrors.route} required>
                                <InputLabel>Route</InputLabel>
                                <Select
                                    value={formData.route}
                                    onChange={(e) => handleFormChange('route', e.target.value)}
                                    label="Route"
                                >
                                    {routeOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth error={!!formErrors.temptype} required>
                                <InputLabel>Template Type</InputLabel>
                                <Select
                                    value={formData.temptype}
                                    onChange={(e) => handleFormChange('temptype', e.target.value)}
                                    label="Template Type"
                                >
                                    {templateOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Template ID"
                                value={formData.tempid}
                                onChange={(e) => handleFormChange('tempid', e.target.value)}
                                placeholder="Enter template ID from gateway"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Send URL"
                                value={formData.sendurl}
                                onChange={(e) => handleFormChange('sendurl', e.target.value)}
                                placeholder="API endpoint URL"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Default Message Template"
                                multiline
                                rows={3}
                                column={6}
                                value={formData.message}
                                onChange={(e) => handleFormChange('message', e.target.value)}
                                placeholder="Enter default message template with placeholders"
                                helperText="Use {name}, {otp}, {school} as placeholders"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button onClick={() => setOpenDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                        {dialogMode === 'add' ? 'Create' : 'Update'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Test SMS Dialog */}
            <Dialog
                open={testDialogOpen}
                onClose={() => !sendingTest && setTestDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <SendIcon color="primary" />
                        <Typography variant="h6">Send Test SMS</Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                        Using configuration: {selectedConfig?.username} - {selectedConfig?.senderid}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Mobile Number"
                        value={testMobile}
                        onChange={(e) => setTestMobile(e.target.value)}
                        placeholder="Enter 10-digit mobile number"
                        sx={{ mt: 2 }}
                        helperText="Enter the mobile number to send test SMS"
                    />
                    <TextField
                        fullWidth
                        label="Message"
                        multiline
                        rows={4}
                        value={testMessage}
                        onChange={(e) => setTestMessage(e.target.value)}
                        sx={{ mt: 2 }}
                        helperText="Edit the message before sending test SMS"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setTestDialogOpen(false)}
                        disabled={sendingTest}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={sendTestSms}
                        disabled={sendingTest || !testMobile.trim()}
                        startIcon={sendingTest ? <CircularProgress size={20} /> : <SendIcon />}
                    >
                        {sendingTest ? "Sending..." : "Send Test SMS"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the SMS configuration?
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Username: {configToDelete?.username}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Sender ID: {configToDelete?.senderid}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={confirmDelete}
                        disabled={loading}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Floating Action Button */}
            <ActionFab
                color="primary"
                onClick={handleAddClick}
                disabled={loading}
            >
                <AddIcon />
            </ActionFab>

            <Snackbar
                open={!!success || !!error}
                autoHideDuration={6000}
                onClose={() => {
                    setSuccess('');
                    setError('');
                }}
            >
                <Alert severity={success ? 'success' : 'error'} onClose={() => {
                    setSuccess('');
                    setError('');
                }}>
                    {success || error}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default SmsConfigViewPage;